import Link from 'next/link'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { useEffect } from 'react'

import useAuth from '@/api/hooks/useAuth'
import PRODUCT_AS_FANSI_KIND from '@/constants/productAsFansiKind'
import useBasket from '@/graphql/hooks/useBasket'
import formatPrice from '@/helpers/formatPrice'
import uuid from '@/helpers/uuid'
import CreatorReference from '@/molecules/CreatorReference'
import Cover from '@/molecules/OrderItem/includes/Cover'
import FeaturedProducts from '@/organisms/FeaturedProducts'
import { useSimilarProducts } from '@/organisms/FeaturedProducts/logic/useSimilarProducts'
import FloatBox from '@/organisms/FloatBox'
import { OrderItem } from '@/ts/Order'

import Header from '../customer/dashboard/includes/Content/includes/Orders/includes/History/includes/Header'
import handleGTMDetails from './functions/handleGTMDetails'
import DeliveryAddressBox from './includes/DeliveryAddressBox'
import Paragraph from './includes/Paragraph'

function OrderPlacedView() {
  const { t } = useTranslation('global')
  const router = useRouter()
  const id = Number(router.query.basket_id)
  const { data: similarProducts } = useSimilarProducts()
  const { basket } = useBasket(id)
  const { data: user } = useAuth('user')

  const deliveryAddress =
    basket?.baskets_delivery_addresses[0]?.delivery_address

  const userId = basket?.user_id

  useEffect(() => {
    if (id) handleGTMDetails(id)
  }, [])

  return (
    <div className="space-y-8 box">
      <div className="flex flex-col items-center justify-center my-12 space-y-4 text-center">
        <h1 className="max-w-xs mx-auto text-xl font-medium text-black/60 sm:text-3xl">
          {t('order_placed_title')}
        </h1>
        <Paragraph user={user} />
      </div>
      <div className="flex flex-col justify-center p-8 space-y-4 md:space-x-4 md:space-y-0 md:flex-row">
        {user?.id === userId && basket && (
          <FloatBox className="space-y-8 md:w-2/3">
            {basket?.orders?.map(
              (order: {
                orders_products: OrderItem[]
                created_at: string
                orders: OrderItem
              }) => (
                <div key={uuid()}>
                  <Header orderDate={order.created_at} />
                  {order?.orders_products?.map((item: OrderItem) => {
                    const product = item?.product

                    return (
                      <Link href={`/products/${product?.slug}`} key={uuid()}>
                        <div className="my-8 cursor-pointer">
                          <CreatorReference creator={product?.creator} />
                          <div className="flex py-4 border-b-2 border-sbtgray-150">
                            <Cover name={product?.name} src={product?.cover} />
                            <div className="mx-8 space-y-4">
                              <div>
                                <p className="font-semibold text-gray-150">
                                  {product?.name}
                                </p>
                                <p className=" text-gray-150">
                                  {t(PRODUCT_AS_FANSI_KIND[product?.type])}
                                </p>
                              </div>
                              {product?.type !== 'Product::FansiExperience' && (
                                <p className="text-gray-100 text:sm text-md">
                                  {formatPrice(product?.price?.value)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              )
            )}
          </FloatBox>
        )}
        {deliveryAddress && (
          <div className="w-full h-full md:w-1/3 md:sticky md:top-5">
            {user?.id === userId && (
              <DeliveryAddressBox deliveryAddress={deliveryAddress} />
            )}
          </div>
        )}
      </div>
      <FeaturedProducts products={similarProducts?.products} />
    </div>
  )
}

export default OrderPlacedView
